import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';


function EvidenceModal({ objectiveId, objectiveText, classes, discipline = 'stem', evidence, onClose, onSave }) {
  const [formData, setFormData] = useState({
    class_id: '',
    title: '',
    description: '',
    level: '',
    reach: 'Partial grade',
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Log what objectiveId we received
  useEffect(() => {
    console.log('[EvidenceModal] Opened with objectiveId:', objectiveId, 'type:', typeof objectiveId);
  }, [objectiveId]);

  useEffect(() => {
    if (evidence) {
      setFormData({
        class_id: evidence.class_id,
        title: evidence.title,
        description: evidence.description || '',
        level: evidence.level || '',
        reach: evidence.reach || 'Partial grade',
        image: null
      });
      if (evidence.image_path) {
        setImagePreview(`${config.baseUrl}${evidence.image_path}`);
      }
    }
  }, [evidence]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.class_id || !formData.title) {
      alert('Please fill in required fields');
      return;
    }

    setSubmitting(true);

    try {
      let imageBase64 = null;
      
      // Convert image to base64 if present
      if (formData.image) {
        const reader = new FileReader();
        imageBase64 = await new Promise((resolve) => {
          reader.onloadend = () => {
            resolve(reader.result.split(',')[1]); // Remove data:image/jpeg;base64, prefix
          };
          reader.readAsDataURL(formData.image);
        });
      }
      
      const data = {
        objective_id: objectiveId,
        class_id: formData.class_id,
        title: formData.title,
        description: formData.description,
        level: formData.level || null,
        reach: formData.reach || 'Partial grade',
        discipline: discipline,
        imageBase64: imageBase64
      };

      console.log('[EvidenceModal] Submitting evidence with objectiveId:', objectiveId, 'type:', typeof objectiveId);


      const config = { 
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      };

      if (evidence) {
        await axios.put(`/outcomes/api/evidence/${evidence.id}`, data, config);
      } else {
        await axios.post('/outcomes/api/evidence', data, { ...config, withCredentials: true });
      }

      onSave();
    } catch (error) {
      console.error('Failed to save evidence:', error);
      alert('Failed to save evidence');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{evidence ? 'Edit Evidence' : 'Add Evidence'}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="objective-context">
              <strong>Objective:</strong> {objectiveText}
            </div>

            <div className="form-group">
              <label>Class *</label>
              <select
                name="class_id"
                value={formData.class_id}
                onChange={handleChange}
                required
              >
                <option value="">Select a class...</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Bridge Building Project"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Additional details about this evidence..."
              />
            </div>

            <div className="form-group">
              <label>Level</label>
              <select
                name="level"
                value={formData.level}
                onChange={handleChange}
              >
                <option value="">Select a level...</option>
                <option value="explore">Explore</option>
                <option value="apply">Apply</option>
                <option value="independently implement">Independently Implement</option>
              </select>
            </div>

            <div className="form-group">
              <label>Reach *</label>
              <select
                name="reach"
                value={formData.reach}
                onChange={handleChange}
                required
              >
                <option value="Partial grade">Partial grade</option>
                <option value="Whole grade">Whole grade</option>
              </select>
            </div>

            <div className="form-group">
              <label>Image (optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="current-image"
                />
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EvidenceModal;
